import React, { useRef, useState, Suspense, useEffect, useMemo, useCallback } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Points, PointMaterial, Preload } from "@react-three/drei";
import * as random from "maath/random/dist/maath-random.esm";
import { getDeviceTier, getStarCount, shouldRenderStars, getPerformanceSettings } from "../utils/performanceDetection";

const Stars = React.memo(({ deviceTier, maxStars }) => {
  const ref = useRef();

  // Debounced Resize Hook
  const useDebouncedResize = (callback, delay) => {
    useEffect(() => {
      const handler = setTimeout(callback, delay);
      return () => clearTimeout(handler);
    }, [callback, delay]);
  };

  // Function to calculate the number of stars based on screen size and device tier
  const calculateNumberOfStars = useCallback(() => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const screenArea = screenWidth * screenHeight;
    
    // Use the improved getStarCount function that considers both device and screen
    const finalStars = getStarCount(deviceTier, screenArea);
    
    return finalStars;
  }, [deviceTier]);

  // Store the number of stars dynamically based on window size and device tier
  const [numStars, setNumStars] = useState(() => calculateNumberOfStars());

  useDebouncedResize(() => {
    const newStarCount = calculateNumberOfStars();
    if (newStarCount !== numStars) {
      setNumStars(newStarCount);
    }
  }, 300); // 300ms debounce delay

  // Function to validate positions to ensure no NaN values
  const validatePositions = (positions) => {
    for (let i = 0; i < positions.length; i++) {
      if (isNaN(positions[i])) {
        console.error(`NaN detected at index ${i}:`, positions[i]);
        return false; // Invalid positions
      }
    }
    return true; // Valid positions
  };

  // Generate the sphere with positions - memoized for performance
  const sphere = useMemo(() => {
    let positions = random.inSphere(new Float32Array(numStars * 3), { radius: 1.2 });

    // Check if the generated positions contain any NaN values
    if (!validatePositions(positions)) {
      positions = random.inSphere(new Float32Array(numStars * 3), { radius: 1.2 }); // Regenerate if NaN found
    }
    
    return positions;
  }, [numStars]);

  // Rotate the stars in the frame - balanced speed for smooth movement
  useFrame((state, delta) => {
    if (ref.current) {
      // Stars move at balanced speed - not too slow, not too fast
      ref.current.rotation.x -= delta / 12; // Balanced X rotation
      ref.current.rotation.y -= delta / 18; // Balanced Y rotation
    }
  });

  return (
    <group rotation={[0, 0, Math.PI / 4]}>
      <Points ref={ref} positions={sphere} stride={3} frustumCulled>
        <PointMaterial
          transparent
          color="#f272c8"
          size={0.005} // Keep original star size for visual consistency
          sizeAttenuation
          depthWrite={false}
        />
      </Points>
    </group>
  );
});

// Move device detection outside component to prevent re-calculations
const DEVICE_TIER = getDeviceTier();
const MAX_STARS = getStarCount(DEVICE_TIER);
const PERFORMANCE_SETTINGS = getPerformanceSettings(DEVICE_TIER);

const StarsCanvas = React.memo(() => {
  // Use pre-calculated values to prevent re-calculations
  const deviceTier = DEVICE_TIER;
  const maxStars = MAX_STARS;
  const performanceSettings = PERFORMANCE_SETTINGS;

  // Always render stars - just with different counts and quality based on device tier

  return (
    <div
      className="absolute inset-0 w-full h-full z-[-1]"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none", // Guarantees buttons are clickable
        overflow: "hidden", // Prevents stars from extending beyond where they should
        zIndex: -1
      }}
    >
      <Canvas 
        camera={{ position: [0, 0, 1] }}
        dpr={performanceSettings.dpr}
        frameloop="always" // Keep movement on all devices
        gl={{ 
          antialias: performanceSettings.antialias,
          alpha: true,
          powerPreference: "high-performance"
        }}
      >
        <Suspense fallback={null}>
          <Stars deviceTier={deviceTier} maxStars={maxStars} />
        </Suspense>
        <Preload all />
      </Canvas>
    </div>
  );
});

export default StarsCanvas;
